import posthog from 'posthog-js';

export interface PosthogIdentityUser {
    id?: string;
    email?: string;
    full_name?: string;
    is_superuser?: boolean;
    organization_id?: string;
    organization_name?: string;
}

function isPosthogEnabled(): boolean {
    return Boolean(import.meta.env.VITE_POSTHOG_KEY);
}

function resolveDistinctId(user: PosthogIdentityUser): string | null {
    if (user.id) return String(user.id);
    if (user.email) return String(user.email).toLowerCase();
    return null;
}

/**
 * Identify the currently authenticated user in PostHog so person profiles
 * are stable and map back to app users.
 */
export function identifyPosthogUser(user: PosthogIdentityUser): void {
    if (!isPosthogEnabled()) return;

    const distinctId = resolveDistinctId(user);
    if (!distinctId) return;

    const personProperties: Record<string, unknown> = {
        user_id: user.id || null,
        email: user.email || null,
        full_name: user.full_name || null,
        organization_id: user.organization_id || null,
        organization_name: user.organization_name || null,
        is_superuser: Boolean(user.is_superuser),
        user_type: user.is_superuser ? 'superadmin' : 'user',
    };

    // PostHog reserved person properties for people UI.
    if (user.email) {
        personProperties.$email = user.email;
    }
    if (user.full_name) {
        personProperties.$name = user.full_name;
    } else if (user.email) {
        personProperties.$name = user.email;
    }

    posthog.identify(distinctId, personProperties);

    if (user.organization_id) {
        posthog.group('organization', user.organization_id, {
            organization_name: user.organization_name || null,
        });
    }
}

/**
 * Clears PostHog identity and anonymous cookies on logout/auth loss.
 */
export function resetPosthogIdentity(): void {
    if (!isPosthogEnabled()) return;
    posthog.reset();
}

